import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoVacio, ListaEsqueleto } from '../../components/ui/Estados';
import { Input, Select } from '../../components/ui/Input';
import { Tabla, type Columna } from '../../components/ui/Tabla';
import { Pestanas } from '../../components/ui/Pestanas';
import { BarraFiltros } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import {
  repositorioAutomatizacion,
  type DatosReglaHoraria,
  type ReglaHoraria,
  type ReglaPorEvento,
} from '../../../data/repositories/RepositorioAutomatizacion';
import { repositorioActuadores } from '../../../data/repositories/RepositorioActuadores';
import { DIAS_SEMANA } from '../../../data/repositories/RepositorioUmbrales';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useSesionStore } from '../../stores/sesionStore';
import { FormularioReglaHoraria } from './FormularioReglaHoraria';
import estilos from '../umbrales/Umbrales.module.css';
import { FichaRegistro } from '../../components/layout/FichaRegistro';

type Pestana = 'horario' | 'evento';

/** Traduce la orden guardada a lenguaje llano. */
function describirOrden(tipoOrden: string, carga: Record<string, unknown>): string {
  if (tipoOrden === 'pulse') {
    const ms = carga.pulse_ms;
    return ms ? `Pulso de ${String(ms)} ms` : 'Pulso';
  }
  return String(carga.v ?? '').toUpperCase() === 'OFF' ? 'Apagarlo' : 'Encenderlo';
}

function describirDias(dias: readonly number[]): string {
  if (dias.length === 7) return 'Todos los días';
  if (dias.length === 0) return 'Ningún día';
  return dias.map((d) => DIAS_SEMANA[d]?.slice(0, 3) ?? '').join(', ');
}

/**
 * Automatismos de los actuadores.
 *
 * Reúne las dos formas de que un equipo se accione solo: por reloj y por
 * evento. Verlas juntas es lo que permite responder «¿qué puede mover este
 * equipo sin que yo lo toque?», que con las reglas repartidas por pantallas
 * distintas era difícil de contestar.
 */
export function Automatizacion() {
  const clienteConsultas = useQueryClient();
  const politica = useSesionStore((s) => s.politica);
  const puedeGestionar = politica?.puede('gestionar_umbrales') ?? false;

  const [pestana, setPestana] = useState<Pestana>('horario');

  // Registro cuya ficha de consulta esta abierta.
  const [enFicha, setEnFicha] = useState<ReglaHoraria | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<'' | 'activas' | 'desactivadas'>('');
  const [formularioAbierto, setFormularioAbierto] = useState(false);
  const [enEdicion, setEnEdicion] = useState<ReglaHoraria | null>(null);

  const horarias = useQuery({
    queryKey: ['automatizacion', 'horarias'],
    queryFn: () => repositorioAutomatizacion.listarHorarias(),
  });

  const porEvento = useQuery({
    queryKey: ['automatizacion', 'evento'],
    queryFn: () => repositorioAutomatizacion.listarPorEvento(),
  });

  const actuadores = useQuery({
    queryKey: ['actuadores', {}],
    queryFn: () => repositorioActuadores.listar({}),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['automatizacion'] });
  };

  const guardar = useMutation({
    mutationFn: (datos: DatosReglaHoraria) =>
      enEdicion
        ? repositorioAutomatizacion.actualizarHoraria(enEdicion.id, datos)
        : repositorioAutomatizacion.crearHoraria(datos),
    onSuccess: () => {
      toast.success(enEdicion ? 'Regla actualizada' : 'Regla creada');
      setFormularioAbierto(false);
      setEnEdicion(null);
      refrescar();
    },
    onError: (e) => {
      registrarError('automatizacion.guardar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo guardar la regla');
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => repositorioAutomatizacion.eliminarHoraria(id),
    onSuccess: () => {
      toast.success('Regla eliminada');
      refrescar();
    },
    onError: (e) => {
      registrarError('automatizacion.eliminar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo eliminar la regla');
    },
  });

  const alternarHoraria = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      repositorioAutomatizacion.cambiarActivoHoraria(id, activo),
    onSuccess: refrescar,
    onError: () => toast.error('No se pudo cambiar el estado de la regla'),
  });

  const alternarEvento = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      repositorioAutomatizacion.cambiarActivoPorEvento(id, activo),
    onSuccess: refrescar,
    onError: () => toast.error('No se pudo cambiar el estado de la regla'),
  });

  const listaHorarias = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    return (horarias.data ?? []).filter((r) => {
      if (estadoFiltro === 'activas' && !r.activo) return false;
      if (estadoFiltro === 'desactivadas' && r.activo) return false;
      return (
        !t || r.actuadorNombre.toLowerCase().includes(t) || r.ubicacion.toLowerCase().includes(t)
      );
    });
  }, [horarias.data, busqueda, estadoFiltro]);

  const listaEventos = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    return (porEvento.data ?? []).filter((r) => {
      if (estadoFiltro === 'activas' && !r.activo) return false;
      if (estadoFiltro === 'desactivadas' && r.activo) return false;
      return !t || r.actuadorNombre.toLowerCase().includes(t);
    });
  }, [porEvento.data, busqueda, estadoFiltro]);

  const sinPermiso = puedeGestionar ? null : 'Tu rol no puede modificar la configuración';

  const columnasHorarias: Columna<ReglaHoraria>[] = [
    {
      clave: 'equipo',
      encabezado: 'Equipo',
      movil: 'titulo',
      render: (r) => <span className={estilos.sensor}>{r.actuadorNombre}</span>,
    },
    {
      clave: 'ubicacion',
      encabezado: 'Ubicación',
      movil: 'subtitulo',
      render: (r) => <span className={estilos.ubicacion}>{r.ubicacion}</span>,
    },
    {
      clave: 'franja',
      encabezado: 'Franja',
      render: (r) => (
        <span className={estilos.rango}>
          {r.horaInicio}–{r.horaFin}
        </span>
      ),
    },
    { clave: 'dias', encabezado: 'Días', render: (r) => describirDias(r.dias) },
    {
      clave: 'orden',
      encabezado: 'Qué hace',
      render: (r) => (
        <Badge color="var(--status-info)">{describirOrden(r.tipoOrden, r.carga)}</Badge>
      ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      render: (r) =>
        r.activo ? (
          <Badge color="var(--status-success)">Activa</Badge>
        ) : (
          <Badge color="var(--text-3)">Inactiva</Badge>
        ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      movil: 'accion',
      render: (r) => (
        <div className={estilos.acciones}>
          <Button
            tamano="sm"
            variante="secundario"
            icono={r.activo ? 'interruptorOn' : 'interruptorOff'}
            title={r.activo ? 'Desactivar' : 'Activar'}
            motivoDeshabilitado={sinPermiso}
            onClick={() => alternarHoraria.mutate({ id: r.id, activo: !r.activo })}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="ver"
            title="Ver datos"
            onClick={() => setEnFicha(r)}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="editar"
            title="Editar"
            motivoDeshabilitado={sinPermiso}
            onClick={() => {
              setEnEdicion(r);
              setFormularioAbierto(true);
            }}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="eliminar"
            title="Eliminar"
            motivoDeshabilitado={sinPermiso}
            onClick={() => {
              const seguro = window.confirm(
                `¿Eliminar esta regla horaria de «${r.actuadorNombre}»?\n\n` +
                  'El equipo dejará de accionarse solo en esa franja.',
              );
              if (seguro) eliminar.mutate(r.id);
            }}
          />
        </div>
      ),
    },
  ];

  const columnasEventos: Columna<ReglaPorEvento>[] = [
    {
      clave: 'equipo',
      encabezado: 'Equipo',
      movil: 'titulo',
      render: (r) => <span className={estilos.sensor}>{r.actuadorNombre}</span>,
    },
    {
      clave: 'origen',
      encabezado: 'Lo dispara',
      movil: 'subtitulo',
      render: (r) => (
        <span className={estilos.ubicacion}>
          Umbral {r.tipoUmbral === 'digital' ? 'digital' : 'analógico'}
        </span>
      ),
    },
    {
      clave: 'orden',
      encabezado: 'Qué hace',
      render: (r) => (
        <Badge color="var(--status-info)">{describirOrden(r.tipoOrden, r.carga)}</Badge>
      ),
    },
    {
      clave: 'restablecer',
      encabezado: 'Se deshace solo',
      render: (r) =>
        r.restablecerAutomatico ? (
          <Badge color="var(--status-success)">Sí</Badge>
        ) : (
          <Badge color="var(--text-3)">No</Badge>
        ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      render: (r) =>
        r.activo ? (
          <Badge color="var(--status-success)">Activa</Badge>
        ) : (
          <Badge color="var(--text-3)">Inactiva</Badge>
        ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      movil: 'accion',
      render: (r) => (
        <div className={estilos.acciones}>
          <Button
            tamano="sm"
            variante="secundario"
            icono={r.activo ? 'interruptorOn' : 'interruptorOff'}
            title={r.activo ? 'Desactivar' : 'Activar'}
            motivoDeshabilitado={sinPermiso}
            onClick={() => alternarEvento.mutate({ id: r.id, activo: !r.activo })}
          />
        </div>
      ),
    },
  ];

  const cargando = pestana === 'horario' ? horarias.isLoading : porEvento.isLoading;
  const error = pestana === 'horario' ? horarias.isError : porEvento.isError;
  const vacio = pestana === 'horario' ? listaHorarias.length === 0 : listaEventos.length === 0;

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo="Automatización"
        descripcion="Reglas que accionan equipos sin intervención de nadie."
        acciones={
          <>
            <Button
              variante="secundario"
              icono="refrescar"
              compactoEnMovil
              onClick={refrescar}
              cargando={horarias.isFetching || porEvento.isFetching}
            >
              Actualizar
            </Button>
            {pestana === 'horario' && (
              <Button
                icono="agregar"
                compactoEnMovil
                motivoDeshabilitado={sinPermiso}
                onClick={() => {
                  setEnEdicion(null);
                  setFormularioAbierto(true);
                }}
              >
                Nueva regla
              </Button>
            )}
          </>
        }
      />

      <Pestanas
        etiqueta="Tipo de automatismo"
        activa={pestana}
        onCambiar={setPestana}
        pestanas={[
          { valor: 'horario', etiqueta: 'Por horario', cuenta: horarias.data?.length ?? 0 },
          { valor: 'evento', etiqueta: 'Por alarma', cuenta: porEvento.data?.length ?? 0 },
        ]}
      />

      <BarraFiltros>
        <Input
          etiqueta="Buscar"
          icono="buscar"
          placeholder="Equipo o ubicación"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <Select
          etiqueta="Estado"
          placeholder="Todas"
          value={estadoFiltro}
          onChange={(e) => setEstadoFiltro(e.target.value as '' | 'activas' | 'desactivadas')}
          opciones={[
            { valor: 'activas', etiqueta: 'Activas' },
            { valor: 'desactivadas', etiqueta: 'Desactivadas' },
          ]}
        />
      </BarraFiltros>

      {cargando ? (
        <ListaEsqueleto filas={5} />
      ) : error ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : vacio ? (
        <EstadoVacio
          icono="horario"
          titulo={pestana === 'horario' ? 'Sin reglas horarias' : 'Sin reglas por alarma'}
          descripcion={
            pestana === 'horario'
              ? 'Crea una regla para que un equipo se encienda o se apague solo a determinadas horas.'
              : 'Estas reglas se crean al configurar un umbral, en la sección de acciones automáticas.'
          }
        />
      ) : pestana === 'horario' ? (
        <>
          <Tabla
            datos={listaHorarias}
            columnas={columnasHorarias}
            claveFila={(r) => r.id}
            etiqueta="Reglas horarias"
          />

          <FichaRegistro
            item={enFicha}
            titulo="Regla horaria"
            columnas={columnasHorarias}
            onCerrar={() => setEnFicha(null)}
          />
        </>
      ) : (
        <Tabla
          datos={listaEventos}
          columnas={columnasEventos}
          claveFila={(r) => r.id}
          etiqueta="Reglas por alarma"
        />
      )}

      <FormularioReglaHoraria
        abierto={formularioAbierto}
        regla={enEdicion}
        actuadores={actuadores.data ?? []}
        guardando={guardar.isPending}
        onCerrar={() => {
          setFormularioAbierto(false);
          setEnEdicion(null);
        }}
        onGuardar={(datos) => guardar.mutate(datos)}
      />
    </div>
  );
}
